import { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as turf from '@turf/turf';
import { Button } from 'reactstrap';
import { useWindowSize } from 'react-use';

import { InfoCard, LinkNewTab } from './basic';
import { GreenButton } from './GreenButton';
import SectionsIcon from '../../icons/SectionsIcon';
import CropIcon from '../../icons/CropIcon';
import SuitabilityIcon from '../../icons/SuitabilityIcon';
import { acresPerSqMeter } from '../../../constants/units';
import {
  BREAKPOINT_VALUES,
  BREAKPOINTS,
  useIsMobile,
} from '../../../utils/responsive';
import {
  FALLBACK_FEATURE_ID,
  SIDE_PANEL_WIDTHS,
  SUITABILITY_INPUTS,
} from '../constants';
import CropInfo from './CropInfo';
import { captureEvent } from '../../../utils/analytics';
import { unitForValue } from '../../../utils/formatters';
import { getNonFallbackFeatures, sortTilesetSuitability } from '../helpers';
import PositionedOverMap from '../../basics/PositionedOverMap';

const Container = styled(InfoCard)`
  display: flex;

  font-size: 13px;
  max-width: calc(100vw - 16px);

  @media (min-width: ${BREAKPOINTS.md}) {
    font-size: 15.6px;
  }

  @media (min-width: ${BREAKPOINTS.lg}) {
    max-width: calc(100vw - ${SIDE_PANEL_WIDTHS.lg} - 32px);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #dee2e6;

  font-size: 12px;
  @media (min-width: ${BREAKPOINTS.xs}) {
    font-size: 13px;
  }
  @media (min-width: ${BREAKPOINTS.md}) {
    font-size: 15.6px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  height: 300px;
  padding: 8px;

  /* Prevent this from covering up too much of the map on mobile */
  max-height: calc((100vh - 160px) / 2);
  min-height: 100px;
  overflow: auto;

  @media (min-width: ${BREAKPOINTS.md}) {
    height: 330px;

    padding: 12px 16px 12px 12px;

    max-height: unset;
    min-height: unset;
    overflow-y: unset;
  }
`;

const CropSuitabilityContainer = styled.div`
  max-width: 536px;
`;

const CropInfoContainer = styled.div`
  max-width: 536px;
`;

const SuitabilityInfoContainer = styled.div`
  max-width: 536px;
`;

const CropInfoSection = styled.div`
  flex-grow: 1;
  display: flex;

  max-height: 300px;
  overflow: auto;
`;

const CropSelectorContainer = styled.div`
  flex-shrink: 1;
  margin-right: 8px;

  @media (min-width: ${BREAKPOINTS.xs}) {
    flex-shrink: 0;
  }

  @media (min-width: ${BREAKPOINTS.sm}) {
    margin-right: 16px;
  }
`;

const Row = styled.div`
  :not(:last-child) {
    margin-bottom: 6px;
  }

  @media (min-width: ${BREAKPOINTS.md}) {
    :not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;

const TABS = {
  ACRES: 'SUITABLE_ACRES',
  CROPS: 'CROPS',
  METHODOLOGY: 'SUITABILITY_OVERVIEW',
};

const TabButton = ({ tab, currentTab, setTab }) => {
  const methodologyTitle =
    useWindowSize().width < BREAKPOINT_VALUES.md ? 'Method' : 'Methodology';

  const text = {
    [TABS.ACRES]: 'Suitability',
    [TABS.CROPS]: 'Crop Info',
    [TABS.METHODOLOGY]: methodologyTitle,
  }[tab];

  const Icon = {
    [TABS.ACRES]: SectionsIcon,
    [TABS.CROPS]: CropIcon,
    [TABS.METHODOLOGY]: SuitabilityIcon,
  }[tab];

  return (
    <GreenButton
      sizes={['xs', 'sm', 'md']}
      className="p-2 d-flex flex-column w-100 align-items-center mb-2"
      onClick={() => {
        captureEvent('suitability_tool_tab_click', { tab });
        setTab(tab);
      }}
      active={tab === currentTab}
    >
      <div>{text}</div>
      <div>
        <Icon width={20} height={20} />
      </div>
    </GreenButton>
  );
};

const CropSelector = ({
  sortedTilesetsWithSuitability,
  selectTileset,
  selectedTilesetId,
  showSuitableAcres = false,
}) => {
  return sortedTilesetsWithSuitability.map(
    ({ id, name, displayName, suitableAcres }) => {
      const acresToShow = showSuitableAcres && suitableAcres;

      return (
        <Row
          key={name}
          className="d-flex align-items-center justify-content-between"
        >
          <GreenButton
            sizes={['xs', 'sm']}
            onClick={() => {
              selectTileset(name, { userAction: true });
            }}
            active={id === selectedTilesetId}
          >
            {displayName}
          </GreenButton>

          {/* Don't show suitable acres if the data hasn't loaded yet or if it's 0 */}
          {!!acresToShow && (
            <div className="ml-2">
              {acresToShow} suitable{' '}
              {unitForValue('acres', 'acre')(acresToShow)}
            </div>
          )}
        </Row>
      );
    },
  );
};

const SuitableAcresTab = ({
  sortedTilesetsWithSuitability,
  selectTileset,
  selectedTilesetId,
}) => (
  <CropSuitabilityContainer>
    <Header>
      Preview suitability for sample crops on your land by selecting the crops
      below
    </Header>
    <CropSelector
      sortedTilesetsWithSuitability={sortedTilesetsWithSuitability}
      selectTileset={selectTileset}
      selectedTilesetId={selectedTilesetId}
      showSuitableAcres
    />
  </CropSuitabilityContainer>
);

const CropInfoTab = ({
  sortedTilesetsWithSuitability,
  selectTileset,
  selectedTilesetId,
}) => {
  const selectedTilesetName = useMemo(
    () =>
      sortedTilesetsWithSuitability.find(({ id }) => id === selectedTilesetId)
        ?.name,
    [sortedTilesetsWithSuitability, selectedTilesetId],
  );

  return (
    <CropInfoContainer>
      <CropInfoSection>
        <CropSelectorContainer>
          <CropSelector
            sortedTilesetsWithSuitability={sortedTilesetsWithSuitability}
            selectTileset={selectTileset}
            selectedTilesetId={selectedTilesetId}
          />
        </CropSelectorContainer>
        <CropInfo currentCropName={selectedTilesetName} />
      </CropInfoSection>
    </CropInfoContainer>
  );
};

const SuitabilityInput = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  font-size: 10px;
  grid-gap: 8px;
  margin-bottom: 12px;

  @media (min-width: ${BREAKPOINTS.xs}) {
    grid-template-columns: 1fr 1fr 1fr;
    font-size: 13px;
  }
  @media (min-width: ${BREAKPOINTS.sm}) {
    font-size: 15.6px;
  }

  @media (min-width: ${BREAKPOINTS.md}) {
    grid-gap: 12px;
    margin-bottom: 16px;
  }

  > div {
    border-radius: 3px;
    color: var(--text-color);
    background-color: var(--border-color);
    text-align: center;

    padding: 4px;

    @media (min-width: ${BREAKPOINTS.sm}) {
      padding: 4px 8px;
    }
  }
`;

const SuitabilityInfoTab = () => {
  return (
    <SuitabilityInfoContainer>
      <SuitabilityInput>
        {SUITABILITY_INPUTS.map((inputName) => (
          <div key={inputName}>{inputName}</div>
        ))}
      </SuitabilityInput>
      <div>
        <div>
          Propagate&apos;s crop suitability model is based on crop adaptation to
          12 environmental factors. Reach out for expert support and access to
          our detailed crop suitability analytics for dozens of tree crops.{' '}
          <LinkNewTab href="https://propagateventures.notion.site/Suitability-calculations-0e6b0504e58c400d8801cd418930b94c">
            Learn More
          </LinkNewTab>
        </div>
        <em
          className="d-block font-tiny text-muted my-2"
          style={{
            lineHeight: 1.2,
            letterSpacing: '-0.2px',
          }}
        >
          Disclamer: crop suitability maps are not a recommendation by Propagate
          Group or Overyield. The Information is provided without representation
          or warranty of any kind. All farms should have proper site diligence
          performed to validate on the ground conditions prior to selecting a
          crop. The accuracy of the underlying data will vary by location as we
          improve data availability & the suitability algorithms.
        </em>
      </div>
    </SuitabilityInfoContainer>
  );
};

const InfoPanel = ({
  loadedMap,
  tilesets,
  suitabilityPerCrop,
  selectedTilesetId,
  selectTileset,
  selectedFeatures,
  reset,
  isDrawMode,
}) => {
  const [tab, setTab] = useState(TABS.ACRES);

  const isMobile = useIsMobile();
  const features = getNonFallbackFeatures(loadedMap);

  const tilesetsWithSuitability = tilesets.map((tileset) => ({
    ...tileset,
    ...suitabilityPerCrop[tileset.name],
  }));

  const selectedFeaturesCount = selectedFeatures.length;
  const selectedFeaturesArea = useMemo(
    () =>
      selectedFeatures.length > 0
        ? Math.round(
            turf.area(turf.featureCollection(selectedFeatures)) *
              acresPerSqMeter,
          )
        : 0,
    [selectedFeatures],
  );

  const selectedFeatureIsFallback =
    selectedFeatures.length === 1 &&
    selectedFeatures[0].id === FALLBACK_FEATURE_ID;

  const sortedTilesetsWithSuitability = sortTilesetSuitability(
    tilesetsWithSuitability,
  );

  const windowWidth = useWindowSize().width;

  if (selectedFeaturesCount === 0) {
    // Ensure that the info box is not blocking things on smaller screens
    const style =
      windowWidth < BREAKPOINT_VALUES.xl ? { marginTop: '80px' } : {};

    return (
      <PositionedOverMap placement="top middle" style={style}>
        <Container className="mb-3">
          <div className="my-auto font-large mx-auto text-center">
            {isDrawMode || !features.length
              ? 'Draw your land parcels on the map.'
              : `Select the regions of your farm by ${
                  isMobile ? 'tapping' : 'clicking on'
                } the map.`}
          </div>
        </Container>
      </PositionedOverMap>
    );
  }

  return (
    <PositionedOverMap placement="bottom left">
      <Container className="p-0">
        <div className="border-right p-2">
          {/* TAB SELECTORS */}
          <TabButton setTab={setTab} tab={TABS.ACRES} currentTab={tab} />
          <TabButton setTab={setTab} tab={TABS.CROPS} currentTab={tab} />
          <TabButton setTab={setTab} tab={TABS.METHODOLOGY} currentTab={tab} />
        </div>

        <Body>
          {/* TAB CONTENT */}
          {tab === TABS.ACRES && (
            <SuitableAcresTab
              sortedTilesetsWithSuitability={sortedTilesetsWithSuitability}
              selectTileset={selectTileset}
              selectedTilesetId={selectedTilesetId}
            />
          )}
          {tab === TABS.CROPS && (
            <CropInfoTab
              sortedTilesetsWithSuitability={sortedTilesetsWithSuitability}
              selectTileset={selectTileset}
              selectedTilesetId={selectedTilesetId}
            />
          )}
          {tab === TABS.METHODOLOGY && <SuitabilityInfoTab />}

          {/* Don't show parcel meta if it's just the fallback feature: */}
          {!selectedFeatureIsFallback && (
            <div className="d-flex align-items-center text-muted">
              <div>
                {selectedFeaturesCount}{' '}
                {unitForValue('parcels', 'parcel')(selectedFeaturesCount)},{' '}
                {selectedFeaturesArea}{' '}
                {unitForValue('acres', 'acre')(selectedFeaturesArea)} selected.
              </div>

              {selectedFeaturesCount > 0 && (
                <>
                  <Button
                    size="sm"
                    className="ml-1 p-0"
                    color="link"
                    onClick={reset}
                  >
                    <u>Reset</u>
                  </Button>
                  .
                </>
              )}
            </div>
          )}
        </Body>
      </Container>
    </PositionedOverMap>
  );
};

export default InfoPanel;
